import type { LoyaltyPromotionDto, ProductVariantDto } from "../../../api";
import { getProductLineTotal } from "../../products/utils/get-pricelist-price";
import { resolveEffectiveMultiplier } from "./resolve-effective-multiplier";
import { calculateEarnedPoints } from "./calculate-earned-points";

export interface CartEarnLine {
  id: number;
  name: string;
  multiplier: number;
  points: number;
}

export interface CartEarn {
  lines: CartEarnLine[];
  total: number;
  /** True when at least one line earns a promotion multiplier > 1. */
  hasBonus: boolean;
  /** True when lines earn different multipliers (mixed promo + non-promo cart). */
  isMixed: boolean;
}

interface EarnItem {
  product: ProductVariantDto;
  quantity: number;
}

/**
 * Per-product loyalty earn for a cart, so the checkout can show exactly which
 * products earn the bonus instead of a confusing blended cart multiplier.
 *
 * Each line earns on its own (net) spend × its effective multiplier. `netTotal`
 * (after any points-redemption discount) is allocated across lines by their
 * share of the gross subtotal, so the per-line points still sum to what's paid.
 */
export function calculateCartEarn(
  items: EarnItem[],
  activePromotions: LoyaltyPromotionDto[] | undefined,
  pointsPer100: number,
  netTotal: number,
): CartEarn {
  const grossTotal = items.reduce((sum, it) => sum + Math.max(0, getProductLineTotal(it.product, it.quantity)), 0);
  const netFactor = grossTotal > 0 ? netTotal / grossTotal : 1;

  const lines: CartEarnLine[] = [];
  for (const item of items) {
    const lineTotal = getProductLineTotal(item.product, item.quantity);
    if (lineTotal <= 0) continue;
    const templateId = item.product.productTemplateId;
    const multiplier = templateId != null ? resolveEffectiveMultiplier(templateId, activePromotions) : 1;
    const points = calculateEarnedPoints(lineTotal * netFactor * multiplier, pointsPer100);
    lines.push({
      id: item.product.id,
      name: item.product.displayName ?? item.product.name ?? `Product #${item.product.id}`,
      multiplier,
      points,
    });
  }

  const total = lines.reduce((sum, l) => sum + l.points, 0);
  const multipliers = new Set(lines.map((l) => l.multiplier));
  return {
    lines,
    total,
    hasBonus: lines.some((l) => l.multiplier > 1.0001),
    isMixed: multipliers.size > 1,
  };
}
